/**
 * HREmployees — live employee records from /v1/hr/employees (tables 0069) with
 * status filters, a detail drawer whose substance is the PAYROLL PROFILE
 * HISTORY (the §2.4 audit trail: effective range + changedBy/changeReason on
 * every version), an identity-only create form, and add-a-version from the
 * drawer. Clone of HRTickets' list → drawer → action shape.
 *
 * Data honesty: an employee with no open profile window (a pre-starter) has NO
 * `current` on the wire — the row says "No payroll profile", never a
 * fabricated £0 and never a dash that reads like zero. Salary is integer
 * pence; the shared lib/money formatter renders it. Adding a version
 * SUPERSEDES the open window (the backend closes it at the new effective
 * date) — the form says so above the fields; an overlap is a 409 with its own
 * explanation, not a generic failure.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, Plus } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import {
  usePolledAsync, fetchEmployees, fetchEmployee, createEmployee, fetchEmployeeProfiles, addPayrollProfile, fetchNIPreview, fetchSSPPreview,
  fetchOrgProfiles, fetchOrgDepartments, addOrgProfile, updateEmployeeIdentity,
} from '../../api'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import type { ApiEmployee, ApiPayrollProfile, NIPreview, SSPPreview, ApiOrgProfile, ApiOrgDepartment } from '../../types'
import { money } from '../../../../lib/money'
import { toast } from 'sonner'
import { findingSeverity, ink, neutral, overlay, statusTicket } from '../../../../theme/tokens'

const EMPLOYEE_STATUSES = ['active', 'leaver'] as const
const FILTERS = ['all', ...EMPLOYEE_STATUSES] as const

// Employee status → quiet dot + label, the TicketStatus idiom. Active rides the
// resolved sage pair (an in-good-standing state), leaver the closed grey.
const EMPLOYEE_STATUS: Record<string, { dot: string; label: string }> = {
  active: statusTicket.resolved,
  leaver: statusTicket.closed,
}

function EmployeeStatus({ status }: { status: string }) {
  const cfg = EMPLOYEE_STATUS[status] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium capitalize whitespace-nowrap" style={{ color: cfg.label }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      {status}
    </span>
  )
}

// Wire pay_frequency → display label.
const FREQUENCY_LABEL: Record<string, string> = {
  weekly: 'Weekly', two_weekly: '2-weekly', four_weekly: '4-weekly', monthly: 'Monthly',
}

// The sixteen 2026-27 NI category letters (form input constraint; the backend
// stores what payroll will validate against ruleset data).
const NI_CATEGORIES = ['A', 'B', 'C', 'D', 'E', 'F', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'S', 'V', 'Z'] as const

// Student loan plan labels. THERE IS NO PLAN 3 — HMRC's numbering gap, not
// an omission; do not add one. Postgraduate is NOT a plan value: it is the
// separate concurrent checkbox (an employee can hold Plan 2 AND a
// postgraduate loan at once).
const SL_PLAN_LABEL: Record<string, string> = {
  plan_1: 'Plan 1', plan_2: 'Plan 2', plan_4: 'Plan 4', plan_5: 'Plan 5',
}

// Shown ONLY when the code is not rest-of-UK — a Scottish or Welsh tax figure
// is inexplicable without it. An unknown wire value renders verbatim rather
// than being hidden.
const JURISDICTION_LABEL: Record<string, string> = {
  scotland: 'Scottish rates', wales: 'Welsh rates',
}

// The 0114 CHECK vocabularies, verbatim — the server refuses anything else
// by name, so these maps are display labels, not validation.
const EMPLOYMENT_TYPE_LABEL: Record<string, string> = {
  permanent: 'Permanent', fixed_term: 'Fixed term', temporary: 'Temporary', contractor: 'Contractor',
}
const WORKING_PATTERN_LABEL: Record<string, string> = {
  full_time: 'Full-time', part_time: 'Part-time',
}

function fmtDay(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Effective window label: to is EXCLUSIVE; an absent to is the open current
// window. Structural param: both ApiPayrollProfile and NIPreview's profile
// echo satisfy it.
function fmtWindow(p: { effectiveFrom: string; effectiveTo?: string }): string {
  return p.effectiveTo ? `${fmtDay(p.effectiveFrom)} – ${fmtDay(p.effectiveTo)}` : `Current since ${fmtDay(p.effectiveFrom)}`
}

const inputCls = 'w-full h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'
const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'

// ── Add-a-version form (inside the drawer) ─────────────────────────────────────

// Exported for its component tests only — the page renders it via the drawer.
export function AddVersionForm({ employee, current, emergencyCode, onDone, onCancel }: {
  employee: ApiEmployee
  current: ApiPayrollProfile | null
  // The year's emergency tax code from the preview payload (PAYErout §12;
  // P9X notifies the value). Null when no preview has landed or the payload
  // predates the field — the write-time hint renders only when present.
  emergencyCode?: string | null
  onDone: () => void
  onCancel: () => void
}) {
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [niCategory, setNICategory] = useState('A')
  const [payFrequency, setPayFrequency] = useState('monthly')
  const [salaryPounds, setSalaryPounds] = useState('')
  const [studentLoanPlan, setStudentLoanPlan] = useState('') // '' = none
  const [postgraduateLoan, setPostgraduateLoan] = useState(false)
  const [taxCode, setTaxCode] = useState('') // '' = no code on record
  const [week1Month1, setWeek1Month1] = useState(false)
  // ERA 1996 s.8(2)(e). NULL means UNANSWERED, and that third state is the
  // entire reason this is a radio pair rather than a checkbox: a checkbox has
  // only two states, so an untouched one submits false indistinguishably from
  // a deliberate "no" — the silent characterisation the whole 0083 design
  // exists to prevent. Only an unselected radio pair makes "not yet answered"
  // visibly distinct, and it is what lets submit stay shut until it is.
  const [payVaries, setPayVaries] = useState<boolean | null>(null)
  const [changeReason, setChangeReason] = useState('')
  const [busy, setBusy] = useState(false)

  const salary = Number(salaryPounds)
  const valid = effectiveFrom !== '' && salaryPounds !== '' && Number.isFinite(salary) && salary >= 0 && changeReason.trim() !== '' && payVaries !== null

  async function submit() {
    setBusy(true)
    try {
      await addPayrollProfile(employee.id, {
        effectiveFrom,
        niCategory,
        payFrequency,
        annualSalaryPence: Math.round(salary * 100),
        // "" when nothing selected — the backend's NULLIF maps it to NULL.
        studentLoanPlan,
        postgraduateLoan,
        // "" when no code entered — NULLIF maps it to NULL exactly as the
        // plan above; the module canonicalises server-side (s1257l → S1257L).
        taxCode,
        week1Month1,
        // Sent EXPLICITLY, always. The backend still coerces a missing field
        // to false, and false is the dangerous direction — it tells an
        // eligibility gate "no hours needed, issue the payslip". `valid`
        // guarantees this is non-null, so this client never sends nil, which
        // is the precondition for tightening the route to a named 400.
        payVariesByReferenceToTimeWorked: payVaries as boolean,
        changeReason: changeReason.trim(),
      })
      toast.success('Profile version added')
      onDone()
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error('That effective date overlaps an existing profile period. A new version must start after the current one began; closed history is never rewritten.')
      } else {
        toast.error(apiErrorMessage(e, 'Adding the version failed'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-tile bg-surface-panel border border-edge-panel px-4 py-4 flex flex-col gap-3">
      {/* What saving DOES — the supersede is silent otherwise. */}
      {current ? (
        <div className="text-12 text-ink-secondary leading-relaxed">
          Current version: <span className="font-medium text-ink-primary">{money(current.annualSalaryPence / 100)}</span> · NI {current.niCategory} · {FREQUENCY_LABEL[current.payFrequency] ?? current.payFrequency}, since {fmtDay(current.effectiveFrom)}.
          <span className="block mt-1">Saving ends it on the new effective date — the new version takes over from that day.</span>
        </div>
      ) : (
        <div className="text-12 text-ink-secondary">No payroll profile yet — this becomes the first version.</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="emp-eff-from" className={labelCls}>Effective from</label>
          <input id="emp-eff-from" type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="emp-salary" className={labelCls}>Annual salary (£)</label>
          <input id="emp-salary" type="number" min={0} step={100} value={salaryPounds} onChange={e => setSalaryPounds(e.target.value)} placeholder="34000" className={inputCls} />
        </div>
        <div>
          <label htmlFor="emp-ni" className={labelCls}>NI category</label>
          <select id="emp-ni" value={niCategory} onChange={e => setNICategory(e.target.value)} className={inputCls}>
            {NI_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="emp-freq" className={labelCls}>Pay frequency</label>
          <select id="emp-freq" value={payFrequency} onChange={e => setPayFrequency(e.target.value)} className={inputCls}>
            {Object.entries(FREQUENCY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="emp-sl-plan" className={labelCls}>Student loan plan</label>
          {/* THERE IS NO PLAN 3 — HMRC's numbering gap, not an omission. */}
          <select id="emp-sl-plan" value={studentLoanPlan} onChange={e => setStudentLoanPlan(e.target.value)} className={inputCls}>
            <option value="">None</option>
            {Object.entries(SL_PLAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-end pb-2">
          <label htmlFor="emp-pgl" className="flex items-center gap-2 text-13 text-ink-body cursor-pointer">
            <input id="emp-pgl" type="checkbox" className="rounded" checked={postgraduateLoan}
              onChange={e => setPostgraduateLoan(e.target.checked)} />
            Postgraduate loan
          </label>
        </div>
        <div>
          <label htmlFor="emp-tax-code" className={labelCls}>Tax code</label>
          {/* A TEXT input, not a select: the code space is a grammar (suffix,
              K, BR/D/NT, S/C prefixes), not an enumerable list, and the
              server is the authority — its 400s already name the value and
              the distinct reason. Uppercasing here is display
              canonicalisation, NOT validation; duplicating the grammar
              client-side would be a second source of truth. */}
          <input id="emp-tax-code" type="text" value={taxCode} placeholder="1257L"
            onChange={e => {
              const v = e.target.value.toUpperCase()
              setTaxCode(v)
              if (v === '') setWeek1Month1(false)
            }}
            className={inputCls} />
          {/* Write-time hint for the no-P45 starter (PAYErout §12; P9X
              notifies the value — 1257L for 2026-27). The value comes from
              the preview payload via the drawer, never hardcoded; shown only
              while the field is empty — the W1/M1 checkbox un-disables the
              moment a code is typed, so the instruction is actionable
              in-place. */}
          {taxCode === '' && emergencyCode && (
            <div className="text-11 text-ink-faint mt-1 leading-relaxed">
              New starter without a P45? The emergency code is{' '}
              <span className="font-medium text-ink-secondary">{emergencyCode}</span> — enter it and tick week 1/month 1.
            </div>
          )}
        </div>
        <div className="flex items-end pb-2">
          {/* Courtesy, not duplication: with no code the basis marker
              qualifies nothing, so the checkbox is disabled and cleared —
              UI state derived from one field, stopping a 400 the user
              could not act on. The server's named 400 and the 0079 CHECK
              both remain the authorities. */}
          <label htmlFor="emp-w1m1"
            className={`flex items-center gap-2 text-13 ${taxCode === '' ? 'text-ink-faint cursor-not-allowed' : 'text-ink-body cursor-pointer'}`}>
            <input id="emp-w1m1" type="checkbox" className="rounded" checked={week1Month1} disabled={taxCode === ''}
              onChange={e => setWeek1Month1(e.target.checked)} />
            Week 1/Month 1
          </label>
        </div>
      </div>

      {/* ── ERA 1996 s.8(2)(e): the declaration ────────────────────────────
          A RADIO PAIR WITH NO PRESELECTION, and neither a checkbox nor a
          defaulted select. A checkbox has two states, so an untouched one
          submits false indistinguishably from a deliberate "no" — which is
          exactly the silent characterisation this column exists to prevent.
          Only an unselected pair makes "not yet answered" visible, and it is
          what keeps submit shut until the employer has actually decided.

          The label is the statutory trigger VERBATIM. The hint names the two
          cases people get wrong: a day-rate worker triggers the duty though
          nobody counts hours, and a salaried worker with paid overtime
          triggers it for the overtime alone. */}
      <div>
        <span className={labelCls}>
          Pay varies by reference to time worked
          <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-faint">(required — no default)</span>
        </span>
        <div className="flex items-center gap-5 mt-1">
          <label className="flex items-center gap-2 text-13 text-ink-body">
            <input type="radio" name="emp-pay-varies" value="yes"
              checked={payVaries === true} onChange={() => setPayVaries(true)} />
            Yes
          </label>
          <label className="flex items-center gap-2 text-13 text-ink-body">
            <input type="radio" name="emp-pay-varies" value="no"
              checked={payVaries === false} onChange={() => setPayVaries(false)} />
            No
          </label>
        </div>
        <div className="text-12 text-ink-muted mt-1 leading-relaxed">
          Does the amount of this worker’s pay vary by reference to time worked? Say yes for
          hourly, day-rate and zero-hours workers — a day rate counts, even though nobody counts
          the hours — and for a salaried worker who is paid overtime, where only the overtime
          hours are required. Say no for a fixed salary, including one reduced for unpaid leave
          or statutory sick pay.
        </div>
      </div>

      <div>
        <label htmlFor="emp-reason" className={labelCls}>
          Change reason
          <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-faint">(required — recorded on the audit trail)</span>
        </label>
        <textarea id="emp-reason" rows={2} value={changeReason} onChange={e => setChangeReason(e.target.value)} maxLength={2000}
          placeholder="Why is this changing?" className="w-full px-3 py-2 text-13 leading-relaxed bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent resize-y" />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={busy || !valid}
          className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          <Check size={14} /> {busy ? 'Saving…' : 'Save version'}
        </button>
        <button onClick={onCancel} disabled={busy}
          className="k-btn-secondary flex-1 disabled:opacity-40">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Add-an-org-version form (E360-4) ──────────────────────────────────────────

// Exported for its component tests only — the page renders it via the drawer.
export function AddOrgVersionForm({ employee, current, onDone, onCancel }: {
  employee: ApiEmployee
  current: ApiOrgProfile | null
  onDone: () => void
  onCancel: () => void
}) {
  const [effectiveFrom, setEffectiveFrom] = useState('')
  // HARD RULE: all four substance fields pre-fill from the in-force version.
  // There is NO server-side inheritance — a version asserts exactly what it
  // carries, and an unasserted field lands NULL with no read fallback (the
  // Colin case). Editing only the title must still submit the other three.
  const [jobTitle, setJobTitle] = useState(current?.jobTitle ?? '')
  const [departmentId, setDepartmentId] = useState(current?.departmentId ?? '')
  const [employmentType, setEmploymentType] = useState(current?.employmentType ?? '')
  const [workingPattern, setWorkingPattern] = useState(current?.workingPattern ?? '')
  const [changeReason, setChangeReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [departments, setDepartments] = useState<ApiOrgDepartment[] | null>(null)
  // The server's own overlap refusal, held for the explicit supersede
  // confirm — null while no refusal stands.
  const [supersedePrompt, setSupersedePrompt] = useState<string | null>(null)

  useEffect(() => {
    fetchOrgDepartments().then(setDepartments).catch(() => setDepartments([]))
  }, [])

  // The pre-filled department may be missing from the picker's list — the
  // list is active-only, and a historical link to a since-deactivated
  // department is correct data. An extra option keeps the pre-fill honest
  // instead of silently dropping it.
  const deptList = departments ?? []
  const currentDeptMissing = departmentId !== '' && !deptList.some(d => d.id === departmentId)

  // The server's rule verbatim: effectiveFrom, changeReason, and at least
  // one substance field.
  const valid = effectiveFrom !== '' && changeReason.trim() !== ''
    && (jobTitle.trim() !== '' || departmentId !== '' || employmentType !== '' || workingPattern !== '')

  async function submit(supersede: boolean) {
    setBusy(true)
    try {
      await addOrgProfile(employee.id, {
        effectiveFrom,
        jobTitle: jobTitle.trim(),
        departmentId,
        employmentType,
        workingPattern,
        ...(supersede ? { supersede: true } : {}),
        changeReason: changeReason.trim(),
      })
      toast.success('Org version added')
      onDone()
    } catch (e) {
      const msg = apiErrorMessage(e, 'Adding the org version failed')
      // The supersede round-trip (E360-4 pin): save WITHOUT supersede first;
      // the server's named 400 for an open-window overlap is the honest
      // script, so surface it and offer the dated confirm rather than
      // pre-empting the refusal. Detection keys on the message's own
      // instruction — if the server's copy ever changes, this degrades to
      // the plain toast (message still shown, confirm not offered).
      if (e instanceof ApiError && e.status === 400 && msg.includes('pass supersede=true')) {
        setSupersedePrompt(msg)
      } else {
        toast.error(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-tile bg-surface-panel border border-edge-panel px-4 py-4 flex flex-col gap-3">
      {current ? (
        <div className="text-12 text-ink-secondary leading-relaxed">
          In force: {[
            current.jobTitle,
            current.departmentName || null,
            current.employmentType ? (EMPLOYMENT_TYPE_LABEL[current.employmentType] ?? current.employmentType) : null,
            current.workingPattern ? (WORKING_PATTERN_LABEL[current.workingPattern] ?? current.workingPattern) : null,
          ].filter(Boolean).join(' · ')}, since {fmtDay(current.effectiveFrom)}.
          <span className="block mt-1">All four fields start from that version — a new version records exactly what you save, nothing carries over on its own.</span>
        </div>
      ) : (
        <div className="text-12 text-ink-secondary">No org profile yet — this becomes the first version.</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="org-eff-from" className={labelCls}>Effective from</label>
          <input id="org-eff-from" type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="org-title" className={labelCls}>Job title</label>
          <input id="org-title" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Field Engineer" className={inputCls} />
        </div>
        <div>
          <label htmlFor="org-dept" className={labelCls}>Department</label>
          <select id="org-dept" value={departmentId} onChange={e => setDepartmentId(e.target.value)} className={inputCls}>
            <option value="">Not recorded</option>
            {currentDeptMissing && (
              <option value={departmentId}>{current?.departmentName || 'Current department'}</option>
            )}
            {deptList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="org-emp-type" className={labelCls}>Employment type</label>
          <select id="org-emp-type" value={employmentType} onChange={e => setEmploymentType(e.target.value)} className={inputCls}>
            <option value="">Not recorded</option>
            {Object.entries(EMPLOYMENT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="org-pattern" className={labelCls}>Working pattern</label>
          <select id="org-pattern" value={workingPattern} onChange={e => setWorkingPattern(e.target.value)} className={inputCls}>
            <option value="">Not recorded</option>
            {Object.entries(WORKING_PATTERN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="org-reason" className={labelCls}>
          Change reason
          <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-faint">(required — recorded on the audit trail)</span>
        </label>
        <textarea id="org-reason" rows={2} value={changeReason} onChange={e => setChangeReason(e.target.value)} maxLength={2000}
          placeholder="Why is this changing?" className="w-full px-3 py-2 text-13 leading-relaxed bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent resize-y" />
      </div>

      {supersedePrompt ? (
        /* The declaration-control precedent: closing history is never a
           preselected tick. The server has refused once, its message names
           the open window, and the confirm states the exact consequence. */
        <div className="rounded-tile border border-edge-panel bg-surface-control px-4 py-3 flex flex-col gap-2">
          <div className="text-12 text-ink-secondary leading-relaxed">{supersedePrompt}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setSupersedePrompt(null); void submit(true) }} disabled={busy}
              className="k-btn flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              <Check size={14} /> Close the version starting {fmtDay(current?.effectiveFrom)} on {fmtDay(effectiveFrom)}
            </button>
            <button onClick={() => setSupersedePrompt(null)} disabled={busy}
              className="k-btn-secondary flex-1 disabled:opacity-40">
              Keep it open
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button onClick={() => void submit(false)} disabled={busy || !valid}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={14} /> {busy ? 'Saving…' : 'Save version'}
          </button>
          <button onClick={onCancel} disabled={busy}
            className="k-btn-secondary flex-1 disabled:opacity-40">
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

// ── NI preview (read-only; slice 1 = today, suggested gross) ──────────────────

// §5 statutory band labels for the six earnings slices, in slice order.
const NI_BANDS = [
  { key: 's1', label: 'Up to ST' },
  { key: 's2', label: 'ST → LEL' },
  { key: 's3', label: 'LEL → PT' },
  { key: 's4', label: 'PT → FUST' },
  { key: 's5', label: 'FUST → UEL' },
  { key: 's6', label: 'Above UEL' },
] as const

// The panel renders 404/422 differently, so it needs the status alongside the
// message — the shared apiErrorMessage supplies the message (the server's own
// prose for the rich 404/422 bodies, exactly as before), ApiError the status.
function niErrorInfo(e: unknown): { status: number | null; message: string } {
  return {
    status: e instanceof ApiError ? e.status : null,
    message: apiErrorMessage(e, 'NI preview failed'),
  }
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

// Income-tax degrade codes decided BEFORE the engine is called — gaps in the
// record itself. The remaining codes are post-call outcomes, which can only
// be reached once priors have been supplied. See the visibility rule in
// NIPreviewSection: the difference is when the gap is determined, not how
// severe it is. pay_frequency_unsupported is deliberately NOT listed: the
// server retired that code when every frequency learned to compute on both
// bases (PR 162), so the entry was dead — and a dead pre-call entry is not
// harmless, because it would hide the priors controls on a payload no
// current server can produce.
const PRE_CALL_DEGRADES = new Set(['nt_unimplemented', 'no_paye_tables'])

// money() renders a negative as "£-190.20", putting the sign inside the
// amount where it reads as a typo. A refund is a real, normal outcome on the
// cumulative basis, so it gets the accounting form: a true minus sign (U+2212,
// not a hyphen) ahead of the currency symbol.
function signedMoney(pence: number): string {
  return pence < 0 ? `−${money(Math.abs(pence) / 100)}` : money(pence / 100)
}

// The period-number control speaks TAX WEEKS, not paydays: CWG2 §1.7 has a
// fortnightly employee on a cumulative code use the Week 2 Table for the
// first payment after 5 April, Week 4 for the second, "and so on up to week
// 52", and a four-weekly employee Weeks 4, 8, … 52 — so the weekly-multiple
// frequencies all count the year's 52 tax weeks, and monthly its 12 tax
// months. PR 157 moved the engine bound to match; this map is the client
// mirror: the label, the range bound, and how far the aligned schedule
// advances per payment (the carry-forward increment). Parity is deliberately
// NOT constrained — elapsed-period selection (§13.5) natively lands on odd
// weeks, so only the upper bound is enforced.
//
// `extra` is the frequency's OWN extra payday week beyond 52 — 53 weekly,
// 54 fortnightly, 56 four-weekly, none for monthly (twelve tax months tile
// the year) — a CLOSED map per CWG2 §1.13, admitted server-side by the §14
// route since PR 163. Each frequency admits ONLY its own extra week (53 on
// a fortnightly schedule is not one of its tax weeks), and the range hint
// NAMES the week rather than silently admitting it: entering it invokes
// different processing (non-cumulative, one standalone payment), which the
// user should know they are asking for.
const TAX_PERIOD: Record<string, { label: string; max: number; extra: number; range: string; advance: number; helper: string | null }> = {
  weekly: {
    label: 'Tax week', max: 52, extra: 53, range: '1–52, or 53 for the extra payday', advance: 1,
    helper: null,
  },
  two_weekly: {
    label: 'Tax week', max: 52, extra: 54, range: '1–52, or 54 for the extra payday', advance: 2,
    helper: 'Enter the tax week the payment falls in, not the payday count — fortnightly paydays fall on weeks 2, 4, … 52, and week 54 is the year’s extra payday, taxed on its own outside the cumulative sequence.',
  },
  four_weekly: {
    label: 'Tax week', max: 52, extra: 56, range: '1–52, or 56 for the extra payday', advance: 4,
    helper: 'Enter the tax week the payment falls in, not the payday count — four-weekly paydays fall on weeks 4, 8, … 52, and week 56 is the year’s extra payday, taxed on its own outside the cumulative sequence.',
  },
  monthly: { label: 'Tax month', max: 12, extra: 0, range: '1–12', advance: 1, helper: null },
}

// Unknown vocabulary falls back to the monthly shape (the tightest bound);
// unmapped frequencies degrade pre-call and never show the controls anyway.
function taxPeriodOf(freq: string | null | undefined) {
  return TAX_PERIOD[freq ?? ''] ?? TAX_PERIOD.monthly
}

// Exported for its component tests only — the page renders it via the drawer.
// onEmergencyCode reports the payload's year-keyed emergency code up to the
// drawer (the shared parent), which feeds AddVersionForm's write-time hint —
// the preview response is the only place the value exists client-side.
export function NIPreviewSection({ employeeId, onEmergencyCode }: {
  employeeId: string
  onEmergencyCode?: (code: string) => void
}) {
  const [preview, setPreview] = useState<NIPreview | null>(null)
  const [error, setError] = useState<{ status: number | null; message: string } | null>(null)
  // Slice-2 controls. Mounted with key={employeeId}, so both reset per employee.
  const [on, setOn] = useState(todayISO())
  const [grossOverride, setGrossOverride] = useState<number | null>(null) // integer pence; null = suggested
  const [grossInput, setGrossInput] = useState('')
  // Cumulative-basis priors. null = NOT SUPPLIED, which is a different state
  // from zero: zero is a real first-period figure, and only absence produces
  // the cumulative_priors_required degrade. Applied on blur like gross.
  const [periodNumber, setPeriodNumber] = useState<number | null>(null)
  const [priorPay, setPriorPay] = useState<number | null>(null) // integer pence
  const [priorTax, setPriorTax] = useState<number | null>(null) // integer pence
  const [periodInput, setPeriodInput] = useState('')
  const [priorPayInput, setPriorPayInput] = useState('')
  const [priorTaxInput, setPriorTaxInput] = useState('')
  // Whether this employee's record makes the priors usable at all. Held
  // SEPARATELY from `preview` and never cleared on refetch: each blur commits
  // a prior and refetches, and the results area blanks to "Calculating…"
  // while it does. If the inputs lived in that area they would unmount on
  // every commit — tabbing from one field to the next would lose focus
  // mid-entry, which is precisely the flow these three controls exist for.
  const [priorsApply, setPriorsApply] = useState(false)
  // The record's pay frequency, held beside priorsApply under the same
  // never-cleared-on-refetch rule: the tax-week/tax-month label and bound
  // must not flip to a default while the results area blanks mid-refetch.
  const [priorsFreq, setPriorsFreq] = useState<string | null>(null)

  useEffect(() => {
    setPreview(null)
    setError(null)
    fetchNIPreview(employeeId, on, grossOverride ?? undefined, {
      periodNumber: periodNumber ?? undefined,
      priorPayToDatePence: priorPay ?? undefined,
      priorTaxToDatePence: priorTax ?? undefined,
    })
      .then(p => {
        setPreview(p)
        // Re-seed the control with the effective figure (pounds, 2dp) — the
        // suggested value on first load, the override after a blur-apply.
        setGrossInput((p.grossPence / 100).toFixed(2))
        // ONE RULE: the controls follow the RECORD, not the answer, because
        // they are an input surface — the same principle that puts them above
        // the results conditional rather than inside it. So the split is by
        // WHEN a gap is determined, not by which code names it:
        //
        //   PRE-CALL  — no code on record, a frequency neither basis
        //               implements, a ruleset carrying no tables. These are
        //               properties of the record, decided before the engine
        //               is asked. Nothing has been entered and nothing can be
        //               trapped, so the fields stay hidden: offering them
        //               would promise a fix they cannot deliver.
        //   POST-CALL — a D-code rate past the end of its table. Reaching
        //               this outcome PROVES priors were supplied, because the
        //               engine is only called once they are. Hiding the
        //               controls here would strand the values the user just
        //               typed, leaving a drawer-remount as the only way to
        //               clear them — an input surface that vanishes in
        //               response to its own input.
        //
        // Computed and cumulative_priors_required are unchanged: visible.
        // week1Month1 is checked first because a W1/M1 record computes
        // without priors and must not be offered them. nt_unimplemented is
        // listed as pre-call for correctness of the classification even
        // though it is W1/M1-only and so unreachable on this branch.
        setPriorsApply(!p.profile.week1Month1 && !!p.profile.taxCode &&
          !(p.payeUnavailable !== null && PRE_CALL_DEGRADES.has(p.payeUnavailable.code)))
        setPriorsFreq(p.profile.payFrequency)
        // Year-keyed data for the drawer's write-time hint; absent on
        // payloads captured before the field existed — report nothing then.
        if (p.emergencyCode && onEmergencyCode) onEmergencyCode(p.emergencyCode)
      })
      .catch(e => setError(niErrorInfo(e)))
  }, [employeeId, on, grossOverride, periodNumber, priorPay, priorTax, onEmergencyCode])

  // Applied on BLUR, not per keystroke. Malformed or negative input never
  // reaches the API (it would 400) — the field resets to the last good figure.
  function applyGross() {
    const v = Number(grossInput)
    if (grossInput.trim() === '' || !Number.isFinite(v) || v < 0) {
      setGrossInput(preview ? (preview.grossPence / 100).toFixed(2) : '')
      return
    }
    const pence = Math.round(v * 100)
    if (pence === (grossOverride ?? preview?.grossPence)) return // unchanged — no refetch
    setGrossOverride(pence)
  }

  // The three priors share applyGross's contract: applied on BLUR, and input
  // the API would reject never leaves the browser — the field resets to the
  // last good value instead. Clearing a field to empty means NOT SUPPLIED and
  // is a legitimate way back to the degrade, so it commits null rather than
  // resetting. Zero stays a real, sendable value throughout.
  function applyPence(
    raw: string, current: number | null,
    commit: (v: number | null) => void, reseed: (s: string) => void,
  ) {
    if (raw.trim() === '') {
      if (current !== null) commit(null) // deliberately cleared = not supplied
      return
    }
    const v = Number(raw)
    if (!Number.isFinite(v) || v < 0) { // the api 400s on a negative prior
      reseed(current === null ? '' : (current / 100).toFixed(2))
      return
    }
    const pence = Math.round(v * 100)
    reseed((pence / 100).toFixed(2))
    if (pence !== current) commit(pence)
  }

  // The tax week (or tax month) is a whole 1-based count, not money: a
  // non-integer or a number outside the year's tax weeks would 400, so it
  // never reaches the API. The bound follows the record's frequency — 52
  // tax weeks for the weekly-multiple kinds, 12 tax months for monthly —
  // mirroring the engine's cumulativeMaxPeriods since PR 157, PLUS exactly
  // one value beyond it: the frequency's own extra payday week (CWG2
  // §1.13; the §14 route since PR 163). The map is closed — 53 commits on
  // weekly but reverts on fortnightly, whose extra payday is 54 — so a
  // wrong-frequency extra week never reaches the API either.
  function applyPeriodNumber() {
    if (periodInput.trim() === '') {
      if (periodNumber !== null) setPeriodNumber(null)
      return
    }
    const v = Number(periodInput)
    const { max: maxPeriods, extra } = taxPeriodOf(priorsFreq)
    if (!Number.isInteger(v) || v < 1 || (v > maxPeriods && v !== extra)) {
      setPeriodInput(periodNumber === null ? '' : String(periodNumber))
      return
    }
    if (v !== periodNumber) setPeriodNumber(v)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="k-eyebrow uppercase">NI preview</div>
        <div className="flex items-center gap-2">
          {preview && <div className="text-11 text-ink-faint">as of {fmtDay(preview.on)} · ruleset {preview.ruleset}</div>}
          {/* Native date input emits complete dates only — fire on change. An
              empty value (field cleared) keeps the last date rather than
              silently falling back to today. */}
          <input type="date" value={on} aria-label="Preview date"
            onChange={e => { if (e.target.value) setOn(e.target.value) }}
            className="h-8 px-2 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
        </div>
      </div>

      {/* Year-to-date controls — the three figures the cumulative basis
          needs and no record holds. Rendered only where they could change
          an answer: the record must specify the cumulative basis AND
          carry a tax code. A codeless employee is cumulative too, but no
          priors would ever produce a figure for them, so showing the
          controls there would promise something they cannot deliver —
          the record drives the UI, as it now drives the calculation. */}
      {priorsApply && (
        <div>
          <div className="text-11 text-ink-muted mb-1">Year-to-date figures (for the cumulative basis)</div>
          <div className="grid grid-cols-3 gap-3">
            {/* Labelled by what the engine reads: the TAX WEEK for the
                weekly-multiple frequencies, the tax month for monthly
                (CWG2 §1.7 — see TAX_PERIOD above). The helper renders only
                where the tax week and the payday count diverge: fortnightly
                and four-weekly. Weekly and monthly coincide, so a note
                would be noise. */}
            <label className="flex flex-col gap-1">
              <span className="text-11 text-ink-faint">
                {taxPeriodOf(priorsFreq).label} <span className="text-ink-muted">({taxPeriodOf(priorsFreq).range})</span>
              </span>
              <input type="number" min={1} step={1} value={periodInput} aria-label={taxPeriodOf(priorsFreq).label}
                onChange={e => setPeriodInput(e.target.value)}
                onBlur={applyPeriodNumber}
                className="h-8 px-2 text-13 text-right tabular-nums bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
              {taxPeriodOf(priorsFreq).helper && (
                <span className="text-11 text-ink-faint leading-relaxed">{taxPeriodOf(priorsFreq).helper}</span>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-11 text-ink-faint">Prior pay to date (£)</span>
              <input type="number" min={0} step={0.01} value={priorPayInput} aria-label="Prior pay to date"
                onChange={e => setPriorPayInput(e.target.value)}
                onBlur={() => applyPence(priorPayInput, priorPay, setPriorPay, setPriorPayInput)}
                className="h-8 px-2 text-13 text-right tabular-nums bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-11 text-ink-faint">Prior tax to date (£)</span>
              <input type="number" min={0} step={0.01} value={priorTaxInput} aria-label="Prior tax to date"
                onChange={e => setPriorTaxInput(e.target.value)}
                onBlur={() => applyPence(priorTaxInput, priorTax, setPriorTax, setPriorTaxInput)}
                className="h-8 px-2 text-13 text-right tabular-nums bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
            </label>
          </div>
          <div className="text-11 text-ink-faint mt-1 leading-relaxed">
            All three are needed before income tax can be computed on this basis. Leave any of them empty and the
            figure is withheld rather than answered on a different basis.
          </div>
        </div>
      )}

      {error !== null ? (
        // Three DISTINCT failure renders — never one generic error:
        error.status === 404 ? (
          // No profile in force on the date — the server message names the gap.
          <div className="text-12 text-ink-muted leading-relaxed">{error.message}</div>
        ) : error.status === 422 ? (
          // Cannot calculate — the server message names the cause (no ruleset
          // for the date / unknown category / unmapped frequency).
          <div className="text-12 text-ink-muted leading-relaxed">{error.message}</div>
        ) : (
          <div className="text-12 text-ink-muted">NI preview unavailable.</div>
        )
      ) : preview === null ? (
        <div className="text-12 text-ink-muted">Calculating…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Which version the numbers came from — the resolved profile echo */}
          <div className="rounded-tile border border-edge-panel bg-surface-panel px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-12 font-semibold text-ink-primary">{fmtWindow(preview.profile)}</span>
              <span className="text-13 font-semibold text-ink-primary tabular-nums">{money(preview.profile.annualSalaryPence / 100)}</span>
            </div>
            {/* Student loan and tax code segments in the history card's
                idiom — a field renders only when it says something; absent-
                and-false renders nothing. The tax code here earns its place
                beyond symmetry: when the PAYE block below degrades it shows
                a heading and a sentence but no code, so this echo is the
                surface that says what is actually on record. */}
            <div className="text-12 text-ink-secondary mt-0.5">
              NI {preview.profile.niCategory} · {FREQUENCY_LABEL[preview.profile.payFrequency] ?? preview.profile.payFrequency}
              {(preview.profile.studentLoanPlan || preview.profile.postgraduateLoan) && (
                <> · {[
                  preview.profile.studentLoanPlan ? (SL_PLAN_LABEL[preview.profile.studentLoanPlan] ?? preview.profile.studentLoanPlan) : null,
                  preview.profile.postgraduateLoan ? 'Postgraduate loan' : null,
                ].filter(Boolean).join(' + ')}</>
              )}
              {preview.profile.taxCode && (
                <> · {preview.profile.taxCode}{preview.profile.week1Month1 ? ' W1/M1' : ''}</>
              )}
            </div>
          </div>

          {/* Period gross + where it came from; the division method is shown
              verbatim when suggested — the drift is surfaced, never hidden */}
          <div>
            <div className="flex items-center justify-between gap-3 text-13">
              <span className="text-ink-secondary flex-shrink-0">
                Period gross <span className="text-11 text-ink-faint">({preview.grossSource})</span>
                {grossOverride !== null && (
                  <button onClick={() => setGrossOverride(null)}
                    className="ml-2 text-11 text-ink-muted underline hover:text-ink-secondary transition-colors">
                    use suggested
                  </button>
                )}
              </span>
              <span className="flex items-center gap-2">
                <input type="number" min={0} step={0.01} value={grossInput} aria-label="Period gross (£)"
                  onChange={e => setGrossInput(e.target.value)}
                  onBlur={applyGross}
                  className="w-28 h-8 px-2 text-13 text-right tabular-nums bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
                <span className="font-semibold text-ink-primary tabular-nums">{money(preview.grossPence / 100)}</span>
              </span>
            </div>
            {preview.grossSource === 'suggested' && (
              <div className="text-12 text-ink-muted leading-relaxed mt-0.5">
                {preview.suggested.method} — annual drift {money(preview.suggested.annualDriftPence / 100)}.
              </div>
            )}
          </div>


          {/* The two headline numbers */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="text-11 text-ink-muted">Employee NI</div>
              <div className="text-16 font-bold text-ink-primary tabular-nums">{money(preview.contributions.employeePence / 100)}</div>
            </div>
            <div className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="text-11 text-ink-muted">Employer NI</div>
              <div className="text-16 font-bold text-ink-primary tabular-nums">{money(preview.contributions.employerPence / 100)}</div>
            </div>
          </div>

          {/* Income tax — three wire states, mirrored exactly: paye non-null
              → the figure and its working; payeUnavailable non-null → a code
              IS on record and the server's sentence says why there is no
              figure (quiet inline text, NO severity dot — this is not an
              eligibility finding); both null → no code on record, no block,
              matching the history card and the SL block. Free pay and
              additional pay are NOT symmetric on screen: whichever is
              non-zero is the working that explains taxable pay (on a K code
              taxable EXCEEDS gross — the additional-pay line beside it is
              why); when both are zero (BR and D codes) taxable pay equalling
              the whole floored gross explains itself, so neither renders —
              unlike the SL zero rule, a zero here is an inapplicable field,
              not an answer. */}
          {preview.paye !== null ? (
            <div>
              <div className="text-11 text-ink-muted mb-1">Income tax</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-tile border border-edge-panel px-4 py-3">
                  <div className="text-11 text-ink-muted">Tax due</div>
                  <div className="text-16 font-bold text-ink-primary tabular-nums">{signedMoney(preview.paye.taxPence)}</div>
                  <div className="text-11 text-ink-muted mt-0.5">
                    code {preview.paye.taxCode}
                    {preview.paye.jurisdiction !== 'rest_of_uk' && ` · ${JURISDICTION_LABEL[preview.paye.jurisdiction] ?? preview.paye.jurisdiction}`}
                  </div>
                  {/* A negative tax due is a REFUND, not a deduction (§4.5.1;
                      §7.5 for a first NT period). The sign alone is easy to
                      misread on a money figure, so the word says it. */}
                  {preview.paye.taxPence < 0 && (
                    <div className="text-11 text-ink-secondary mt-0.5">a refund — this period repays tax already deducted</div>
                  )}
                  {/* Cannot fire at 2026-27 rates, but a capped figure that
                      looks uncapped is the worst failure this panel could
                      have — one visible line, never silence. */}
                  {preview.paye.regulatoryLimitApplied && (
                    <div className="text-11 text-ink-secondary mt-0.5">regulatory limit applied — tax capped at 50% of pay</div>
                  )}
                </div>
                {/* The taxable-pay tile reads from whichever field the basis
                    populates. On the cumulative basis taxablePayPence is
                    NULL — the quantity does not exist there (§4.4.4 floors
                    the cumulative figure) — so the tile shows the to-date
                    figure under a to-date LABEL. Never `?? 0`: that would
                    render a real £0.00 for an absence. Free pay carries the
                    same span in its label for the same reason — one quantity,
                    two spans, and an unlabelled £2,096.52 beside a £1,048.26
                    period figure is the ambiguity this slice exists to end. */}
                <div className="rounded-tile border border-edge-panel px-4 py-3">
                  <div className="text-11 text-ink-muted">
                    {preview.paye.basis === 'cumulative' ? 'Taxable pay to date' : 'Taxable pay'}
                  </div>
                  <div className="text-16 font-bold text-ink-primary tabular-nums">
                    {money((preview.paye.basis === 'cumulative'
                      ? preview.paye.taxablePayToDatePence ?? 0
                      : preview.paye.taxablePayPence ?? 0) / 100)}
                  </div>
                  {preview.paye.freePayPence > 0 && (
                    <div className="text-11 text-ink-muted mt-0.5">
                      {preview.paye.basis === 'cumulative' ? 'free pay to date' : 'free pay'} {money(preview.paye.freePayPence / 100)}
                    </div>
                  )}
                  {preview.paye.additionalPayPence > 0 && (
                    <div className="text-11 text-ink-muted mt-0.5">
                      {preview.paye.basis === 'cumulative' ? 'additional pay to date' : 'additional pay'} {money(preview.paye.additionalPayPence / 100)}
                    </div>
                  )}
                </div>
              </div>
              {/* The carry-forward pair — the whole usable feature until a
                  ledger exists: these two ARE the next period's priors, so
                  they are labelled as the inputs they become, not as trivia.
                  Rendered only on the cumulative basis, where they exist. */}
              {preview.paye.basis === 'cumulative' && preview.paye.payToDatePence !== undefined && (
                <div className="rounded-tile border border-edge-row-faint px-4 py-2 mt-3">
                  <div className="flex items-baseline justify-between py-0.5 text-12">
                    <span className="text-ink-secondary">Pay to date</span>
                    <span className="text-ink-primary tabular-nums">{money(preview.paye.payToDatePence / 100)}</span>
                  </div>
                  <div className="flex items-baseline justify-between py-0.5 text-12">
                    <span className="text-ink-secondary">Tax to date</span>
                    <span className="text-ink-primary tabular-nums">{signedMoney(preview.paye.taxToDatePence ?? 0)}</span>
                  </div>
                  {/* The aligned schedule advances by the payment interval
                      measured in tax weeks (CWG2 §1.7): +1 weekly/monthly,
                      +2 fortnightly, +4 four-weekly. Display prose only —
                      never an engine input. At the extra payday the sentence
                      must NOT advance — that payment is the tax year's last
                      (CWG2 §1.13), so "into tax week 56" after a week-54
                      entry would name a week that does not exist; the year's
                      end is stated instead. */}
                  {taxPeriodOf(preview.profile.payFrequency).extra !== 0 &&
                    preview.paye.periodNumber === taxPeriodOf(preview.profile.payFrequency).extra ? (
                    <div className="text-11 text-ink-faint mt-1 leading-relaxed">
                      This is the year’s extra payday — the tax year’s final payment, taxed on its own outside
                      the cumulative sequence — so there is no later tax week to carry these into.
                    </div>
                  ) : (
                    <div className="text-11 text-ink-faint mt-1 leading-relaxed">
                      Carry these two into {taxPeriodOf(preview.profile.payFrequency).label.toLowerCase()}{' '}
                      {(preview.paye.periodNumber ?? 0) + taxPeriodOf(preview.profile.payFrequency).advance} as
                      its prior pay and prior tax to date.
                    </div>
                  )}
                </div>
              )}
              {/* THE TRUST BOUNDARY, on the block itself and not only in the
                  assumptions prose. This is the one place the panel could
                  imply more authority than it has: the figures above rest on
                  numbers the user typed, which nothing checked. */}
              {preview.paye.basis === 'cumulative' && (
                <div className="text-11 text-ink-muted mt-2 leading-relaxed">
                  Computed from the year-to-date figures entered above. Those figures are taken on trust —
                  nothing reconciles them against an earlier period, and nothing stores them.
                </div>
              )}
            </div>
          ) : preview.payeUnavailable !== null ? (
            <div>
              <div className="text-11 text-ink-muted mb-1">Income tax</div>
              <div className="text-12 text-ink-muted leading-relaxed">{preview.payeUnavailable.message}</div>
            </div>
          ) : !preview.profile.taxCode && preview.emergencyCode ? (
            /* Codeless record: the year's emergency code as the next step.
               The value is YEAR-KEYED DATA from the payload — PAYErout §12's
               emergency code, notified on P7X/P9X (1257L for 2026-27 per P9X
               "Tax codes to use from 6 April 2026") — never hardcoded here.
               Payloads captured before the field existed lack the key, and
               this arm then renders nothing: the both-null state stays a
               silent block exactly as it always was. */
            <div>
              <div className="text-11 text-ink-muted mb-1">Income tax</div>
              <div className="text-12 text-ink-muted leading-relaxed">
                No tax code on record, so no income tax is previewed. A new starter without a P45
                takes the emergency code <span className="font-medium text-ink-secondary">{preview.emergencyCode}</span> on
                a week 1/month 1 basis until HMRC issues a code.
              </div>
            </div>
          ) : null}

          {/* Student loan deductions — rendered only when the profile
              carries a plan or a postgraduate loan (the API emits [] for
              neither). ZERO deductions are RENDERED, deliberately: a plan
              below its threshold is a real answer, and hiding the £0.00
              would make an employee on Plan 2 look like they had no plan —
              the threshold line beneath the figure is the working that
              explains the zero. */}
          {preview.studentLoans.length > 0 && (
            <div>
              <div className="text-11 text-ink-muted mb-1">Student loan deductions</div>
              <div className="grid grid-cols-2 gap-3">
                {preview.studentLoans.map(sl => (
                  <div key={sl.plan} className="rounded-tile border border-edge-panel px-4 py-3">
                    <div className="text-11 text-ink-muted">
                      {sl.plan === 'postgraduate' ? 'Postgraduate loan' : (SL_PLAN_LABEL[sl.plan] ?? sl.plan)}
                    </div>
                    <div className="text-16 font-bold text-ink-primary tabular-nums">{money(sl.deductionPence / 100)}</div>
                    <div className="text-11 text-ink-muted mt-0.5">threshold {money(sl.thresholdPence / 100)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* §5 earnings record — the six slices + the RTI signal */}
          <div>
            <div className="text-11 text-ink-muted mb-1">
              §5 earnings record · {preview.steps.recorded ? 'recorded for RTI' : 'not recorded for RTI'}
            </div>
            <div className="rounded-tile border border-edge-row-faint px-4 py-2">
              {NI_BANDS.map(b => (
                <div key={b.key} className="flex items-baseline justify-between py-1 text-12">
                  <span className="text-ink-secondary">{b.label}</span>
                  <span className="text-ink-primary tabular-nums">{money(preview.steps[b.key] / 100)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Eligibility — silence is never a pass, so the empty case says so */}
          <div>
            <div className="text-11 text-ink-muted mb-1">Category eligibility</div>
            {preview.eligibility.length === 0 ? (
              <div className="inline-flex items-center gap-2 text-12 font-medium" style={{ color: statusTicket.resolved.label }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusTicket.resolved.dot }} />
                Category checks passed
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {preview.eligibility.map(f => {
                  const cfg = findingSeverity[f.severity as keyof typeof findingSeverity] ?? findingSeverity.unverifiable
                  return (
                    <div key={f.code} className="flex items-start gap-2">
                      <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: cfg.dot }} />
                      <div className="text-12 leading-relaxed">
                        <span className="font-medium capitalize" style={{ color: cfg.label }}>{f.severity}</span>
                        <span className="text-ink-secondary"> — {f.message}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Assumptions — shown, not hidden */}
          <div>
            <div className="text-11 text-ink-muted mb-1">Assumptions</div>
            <ul className="list-disc pl-4 flex flex-col gap-0.5">
              {preview.assumptions.map(a => (
                <li key={a} className="text-12 text-ink-muted leading-relaxed">{a}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Detail drawer ───────────────────────────────────────────────────────────────

function EmployeeDrawer({ employeeId, allEmployees, onClose, onChanged }: {
  /** The drawer reads its OWN detail — GET /v1/hr/employees/{id} — never the
   *  list row it was opened from (AUTH-1, the structural fix): the row the
   *  page holds may be any shape the list wire happens to carry, and the
   *  identity PUT below full-replaces phone/region from what is read HERE. */
  employeeId: string
  /** The page's list — the manager picker's options (managerId rides the
   *  list wire since the E360-3 rider, so no extra call). */
  allEmployees: ApiEmployee[]
  onClose: () => void
  onChanged: () => void
}) {
  const [employee, setEmployee] = useState<ApiEmployee | null>(null)
  const [employeeError, setEmployeeError] = useState<string | null>(null)
  const [profiles, setProfiles] = useState<ApiPayrollProfile[] | null>(null)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  // Org profile history (E360-4) — the payroll-history mechanics.
  const [orgProfiles, setOrgProfiles] = useState<ApiOrgProfile[] | null>(null)
  const [orgError, setOrgError] = useState<string | null>(null)
  const [addingOrg, setAddingOrg] = useState(false)
  // Manager picker: '' = no manager. Re-seeded when the drawer switches
  // employee or the row refreshes after a save — the adjust-during-render
  // idiom (the state-setter sweep), not an effect.
  const managerSeed = `${employeeId}:${employee?.managerId ?? ''}`
  const [managerSel, setManagerSel] = useState(employee?.managerId ?? '')
  const [seededFrom, setSeededFrom] = useState(managerSeed)
  if (seededFrom !== managerSeed) {
    setSeededFrom(managerSeed)
    setManagerSel(employee?.managerId ?? '')
  }
  const [savingManager, setSavingManager] = useState(false)
  // The year's emergency tax code, reported up by NIPreviewSection from the
  // preview payload (the only client-side source of the value) and fed to
  // AddVersionForm's write-time hint. Null until a preview lands; stays null
  // on pre-field payloads, and the hint then never renders.
  const [emergencyCode, setEmergencyCode] = useState<string | null>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function loadEmployee() {
    try {
      setEmployee(await fetchEmployee(employeeId))
      setEmployeeError(null)
    } catch (e) {
      setEmployeeError(apiErrorMessage(e, 'Failed to load the employee'))
    }
  }
  async function loadHistory() {
    try {
      setProfiles(await fetchEmployeeProfiles(employeeId))
      setHistoryError(null)
    } catch (e) {
      setHistoryError(apiErrorMessage(e, 'Failed to load profile history'))
    }
  }
  async function loadOrgHistory() {
    try {
      setOrgProfiles(await fetchOrgProfiles(employeeId))
      setOrgError(null)
    } catch (e) {
      setOrgError(apiErrorMessage(e, 'Failed to load org history'))
    }
  }
  // One effect, the detail and both histories — independent fetches that
  // share a trigger, and a second effect would double the lint surface.
  useEffect(() => { void loadEmployee(); void loadHistory(); void loadOrgHistory() }, [employeeId]) // eslint-disable-line react-hooks/exhaustive-deps

  // After any write the drawer re-reads its own detail (the derived title,
  // the manager, the identity trio) AND tells the page to refresh its list.
  function changed() {
    void loadEmployee()
    onChanged()
  }

  // HARD RULE (E360-4): the identity PUT is FULL-REPLACE — the server NULLIFs
  // every field it governs, so an omitted phone or region would be CLEARED.
  // Every save echoes all three from the current row; there is no
  // send-only-what-changed path, by design. The cycle guard's named 400s
  // reach the toast verbatim through apiErrorMessage — no special-casing.
  async function saveManager() {
    if (!employee) return // the picker only renders once the detail is read
    setSavingManager(true)
    try {
      await updateEmployeeIdentity(employeeId, {
        phone: employee.phone ?? '',
        region: employee.region ?? '',
        managerId: managerSel,
      })
      toast.success(managerSel === '' ? 'Manager cleared' : 'Manager saved')
      changed()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Saving the manager failed'))
    } finally {
      setSavingManager(false)
    }
  }

  const current = profiles?.find(p => !p.effectiveTo) ?? null
  const orgCurrent = orgProfiles?.find(p => !p.effectiveTo) ?? null
  const managerDirty = managerSel !== (employee?.managerId ?? '')

  // Until the detail read lands there is nothing honest to show but the
  // shell — no name, no fields borrowed from a list row.
  if (!employee) {
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="k-drawer"
          style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
          <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
            <div className="text-12 text-ink-muted">{employeeError ?? 'Loading…'}</div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
              <X size={16} />
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header — identity up top */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span className="k-eyebrow uppercase">{employee.employeeNumber}</span>
              <EmployeeStatus status={employee.status} />
            </div>
            <div className="text-detail-title font-bold text-ink-primary truncate">{employee.fullName}</div>
            {employee.email && <div className="text-12 text-ink-muted truncate">{employee.email}</div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {/* Identity — real fields only; the 0104 trio renders ONLY when
              present (no fake regions, no dash rows). */}
          <div className="grid grid-cols-2 gap-3 text-13">
            <div><div className="text-11 text-ink-muted">Start date</div><div className="text-ink-primary">{fmtDay(employee.startDate)}</div></div>
            <div><div className="text-11 text-ink-muted">Date of birth</div><div className="text-ink-primary">{fmtDay(employee.dateOfBirth)}</div></div>
            {employee.jobTitle && (
              <div><div className="text-11 text-ink-muted">Job title</div><div className="text-ink-primary">{employee.jobTitle}</div></div>
            )}
            {employee.phone && (
              <div><div className="text-11 text-ink-muted">Phone</div><div className="text-ink-primary">{employee.phone}</div></div>
            )}
            {employee.region && (
              <div><div className="text-11 text-ink-muted">Region</div><div className="text-ink-primary">{employee.region}</div></div>
            )}
          </div>

          {/* Reporting line (E360-4) — the one identity field editable here.
              CLEARING is deliberate: "No manager" is an explicit option the
              user must choose, never an empty default — a cleared manager
              detaches this person's whole subtree on the org chart. */}
          <div>
            <label htmlFor="emp-manager" className={labelCls}>Manager</label>
            <div className="flex items-center gap-2">
              <select id="emp-manager" value={managerSel} onChange={e => setManagerSel(e.target.value)} className={inputCls}>
                <option value="">No manager</option>
                {allEmployees
                  .filter(e => e.id !== employee.id)
                  .slice()
                  .sort((a, b) => a.fullName.localeCompare(b.fullName))
                  .map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
              </select>
              {managerDirty && (
                <button onClick={() => void saveManager()} disabled={savingManager}
                  className="k-btn inline-flex items-center gap-2 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Check size={14} /> {savingManager ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {/* Org profile history (E360-4) — the payroll section's shape so
              the two read alike: in-force card first, audit pair on every
              version. Substance renders only what the version ASSERTS —
              an absent field is absent, never inherited (the Colin case). */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="k-eyebrow uppercase">Org profile history</div>
              {!addingOrg && (
                <button onClick={() => setAddingOrg(true)}
                  className="k-btn-secondary k-btn-secondary--flex">
                  <Plus size={12} /> Add version
                </button>
              )}
            </div>

            {addingOrg && (
              <div className="mb-3">
                <AddOrgVersionForm
                  employee={employee}
                  current={orgCurrent}
                  onCancel={() => setAddingOrg(false)}
                  onDone={() => { setAddingOrg(false); void loadOrgHistory(); changed() }}
                />
              </div>
            )}

            {orgError ? (
              <div className="text-12 text-ink-muted">{orgError}</div>
            ) : orgProfiles === null ? (
              <div className="text-12 text-ink-muted">Loading history…</div>
            ) : orgProfiles.length === 0 ? (
              <div className="text-12 text-ink-muted">
                No org profile yet. Job title, department and working pattern are versioned — add the first when the role is set.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {orgProfiles.map(p => {
                  const open = !p.effectiveTo
                  const substance = [
                    p.departmentName || null,
                    p.employmentType ? (EMPLOYMENT_TYPE_LABEL[p.employmentType] ?? p.employmentType) : null,
                    p.workingPattern ? (WORKING_PATTERN_LABEL[p.workingPattern] ?? p.workingPattern) : null,
                  ].filter(Boolean)
                  return (
                    <div key={p.id} className={`rounded-tile border px-4 py-3 ${open ? 'border-edge-panel bg-surface-panel' : 'border-edge-row-faint'}`}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className={`text-12 ${open ? 'font-semibold text-ink-primary' : 'text-ink-secondary'}`}>{fmtWindow(p)}</span>
                        {p.jobTitle && (
                          <span className="text-13 font-semibold text-ink-primary truncate">{p.jobTitle}</span>
                        )}
                      </div>
                      {substance.length > 0 && (
                        <div className="text-12 text-ink-secondary mt-0.5">{substance.join(' · ')}</div>
                      )}
                      {/* The audit pair — who and why, on every version */}
                      <div className="text-12 text-ink-muted mt-1.5">
                        <span className="font-medium text-ink-secondary">{p.changedBy}</span> — {p.changeReason}
                        <span className="text-ink-faint"> · {fmtDay(p.createdAt)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Profile history — the substance: the §2.4 audit trail */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="k-eyebrow uppercase">Payroll profile history</div>
              {!adding && (
                <button onClick={() => setAdding(true)}
                  className="k-btn-secondary k-btn-secondary--flex">
                  <Plus size={12} /> Add version
                </button>
              )}
            </div>

            {adding && (
              <div className="mb-3">
                <AddVersionForm
                  employee={employee}
                  current={current}
                  emergencyCode={emergencyCode}
                  onCancel={() => setAdding(false)}
                  onDone={() => { setAdding(false); void loadHistory(); changed() }}
                />
              </div>
            )}

            {historyError ? (
              <div className="text-12 text-ink-muted">{historyError}</div>
            ) : profiles === null ? (
              <div className="text-12 text-ink-muted">Loading history…</div>
            ) : profiles.length === 0 ? (
              <div className="text-12 text-ink-muted">
                No payroll profile yet. This employee is on the books but carries no payroll data — add the first version when their terms are set.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {profiles.map(p => {
                  const open = !p.effectiveTo
                  return (
                    <div key={p.id} className={`rounded-tile border px-4 py-3 ${open ? 'border-edge-panel bg-surface-panel' : 'border-edge-row-faint'}`}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className={`text-12 ${open ? 'font-semibold text-ink-primary' : 'text-ink-secondary'}`}>{fmtWindow(p)}</span>
                        <span className="text-13 font-semibold text-ink-primary tabular-nums">{money(p.annualSalaryPence / 100)}</span>
                      </div>
                      {/* Student loan segment renders ONLY when a plan is
                          present or postgraduate is true — absent-and-false
                          renders nothing, deliberately: a "no student loan"
                          line on every card would be noise (the list-join
                          was skipped for the same reason). */}
                      <div className="text-12 text-ink-secondary mt-0.5">
                        NI {p.niCategory} · {FREQUENCY_LABEL[p.payFrequency] ?? p.payFrequency}
                        {(p.studentLoanPlan || p.postgraduateLoan) && (
                          <> · {[
                            p.studentLoanPlan ? (SL_PLAN_LABEL[p.studentLoanPlan] ?? p.studentLoanPlan) : null,
                            p.postgraduateLoan ? 'Postgraduate loan' : null,
                          ].filter(Boolean).join(' + ')}</>
                        )}
                        {/* Tax code segment renders ONLY when a code is
                            present — a version with no code renders nothing
                            extra, deliberately: a "no tax code" line on
                            every card would be noise (the server-side
                            list-join was skipped for the same reason). The
                            W1/M1 marker rides the code it qualifies. */}
                        {p.taxCode && (
                          <> · {p.taxCode}{p.week1Month1 ? ' W1/M1' : ''}</>
                        )}
                      </div>
                      {/* The audit pair — who and why, on every version */}
                      <div className="text-12 text-ink-muted mt-1.5">
                        <span className="font-medium text-ink-secondary">{p.changedBy}</span> — {p.changeReason}
                        <span className="text-ink-faint"> · {fmtDay(p.createdAt)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* NI preview — read-only working from the profile in force on the
              chosen date. Keyed so the date/gross controls reset per employee. */}
          <NIPreviewSection key={employee.id} employeeId={employee.id} onEmergencyCode={setEmergencyCode} />
          <SSPPreviewSection key={`ssp-${employee.id}`} employeeId={employee.id} />
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── SSP preview (read-only; AWE is explicit until payroll history derives it) ──

export function SSPPreviewSection({ employeeId }: { employeeId: string }) {
  const [aweInput, setAweInput] = useState('')
  const [priorInput, setPriorInput] = useState('')
  const [preview, setPreview] = useState<SSPPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function calculate() {
    const awe = Number(aweInput)
    const prior = priorInput.trim() === '' ? undefined : Number(priorInput)
    if (!Number.isFinite(awe) || awe <= 0 || (prior !== undefined && (!Number.isFinite(prior) || prior < 0))) {
      setError('Enter a positive average weekly earnings figure and, optionally, prior SSP paid.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      setPreview(await fetchSSPPreview(employeeId, Math.round(awe * 100), prior === undefined ? undefined : Math.round(prior * 100)))
    } catch (e) {
      setPreview(null)
      setError(apiErrorMessage(e, 'SSP preview unavailable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-5 pt-5 border-t border-edge-row">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="k-eyebrow uppercase">SSP preview</div>
        <span className="text-11 text-ink-faint">read-only · 2026/27 rates</span>
      </div>
      <div className="text-12 text-ink-muted leading-relaxed mb-3">
        Average weekly earnings are required for this preview. They are not inferred from salary, and nothing is stored.
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <label className="flex flex-col gap-1">
          <span className={labelCls}>Average weekly earnings (£)</span>
          <input type="number" min={0.01} step={0.01} value={aweInput} aria-label="Average weekly earnings (£)"
            onChange={e => setAweInput(e.target.value)}
            className={inputCls} placeholder="650.00" />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelCls}>Prior SSP paid (£, optional)</span>
          <input type="number" min={0} step={0.01} value={priorInput} aria-label="Prior SSP paid (£)"
            onChange={e => setPriorInput(e.target.value)}
            className={inputCls} placeholder="0.00" />
        </label>
      </div>
      <button onClick={() => void calculate()} disabled={busy}
        className="k-btn mt-3 disabled:opacity-40 disabled:cursor-not-allowed">
        {busy ? 'Calculating…' : 'Calculate SSP preview'}
      </button>
      {error && <div className="text-12 text-ink-muted leading-relaxed mt-3">{error}</div>}
      {preview && (
        <div className="flex flex-col gap-3 mt-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="text-11 text-ink-muted">Weekly rate</div>
              <div className="text-16 font-bold text-ink-primary tabular-nums">{money(preview.weeklyRate / 100)}</div>
            </div>
            <div className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="text-11 text-ink-muted">Entitlement cap</div>
              <div className="text-16 font-bold text-ink-primary tabular-nums">{money(preview.entitlementCap / 100)}</div>
            </div>
            <div className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="text-11 text-ink-muted">Total due</div>
              <div className="text-16 font-bold text-ink-primary tabular-nums">{money(preview.totalDuePence / 100)}</div>
            </div>
          </div>
          <div className="text-12 text-ink-muted">
            {preview.eightyPercentBranch ? '80% AWE branch applied.' : 'Statutory weekly rate branch applied.'}
          </div>
          {preview.periods.map(period => (
            <div key={`${period.start}-${period.end}`} className="rounded-tile border border-edge-panel px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-12 font-semibold text-ink-primary">{fmtDay(period.start)} – {fmtDay(period.end)}</span>
                <span className="text-13 font-semibold text-ink-primary tabular-nums">{money(period.duePence / 100)}</span>
              </div>
              <div className="text-11 text-ink-muted mt-1">
                {period.weeks.length} week{period.weeks.length === 1 ? '' : 's'} · {period.weeks.reduce((n, w) => n + w.payableDays, 0)} payable days
                {period.capReached ? ' · entitlement cap reached' : ''}
                {period.longStopReached ? ' · long stop reached' : ''}
              </div>
            </div>
          ))}
          {preview.periods.length === 0 && <div className="text-12 text-ink-muted">No current sickness absence episodes were found.</div>}
        </div>
      )}
    </div>
  )
}

  // ── Create form (identity only) ────────────────────────────────────────────────

function CreateEmployeeModal({ onClose, onCreated }: { onClose: () => void; onCreated: (e: ApiEmployee) => void }) {
  const [employeeNumber, setEmployeeNumber] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [startDate, setStartDate] = useState('')
  // The 0104 descriptive trio (M7a) — all optional, free text.
  const [jobTitle, setJobTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [region, setRegion] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const valid = employeeNumber.trim() !== '' && fullName.trim() !== '' && startDate !== ''

  async function submit() {
    setBusy(true)
    try {
      const created = await createEmployee({
        employeeNumber: employeeNumber.trim(),
        fullName: fullName.trim(),
        email: email.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
        startDate,
        jobTitle: jobTitle.trim() || undefined,
        phone: phone.trim() || undefined,
        region: region.trim() || undefined,
      })
      toast.success(`${created.fullName} added`)
      onCreated(created)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error('That employee number is already taken.')
      } else {
        toast.error(apiErrorMessage(e, 'Creating the employee failed'))
      }
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      className="flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* S-Tier (slice 5): a centered dialog is a floating widget — .k-card
          (the CloseDealModal/CreateExpenseModal precedent). */}
      <div role="dialog" aria-modal="true" aria-label="Add employee"
        className="k-card w-full max-w-md p-6 gap-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Add employee</div>
            {/* Identity only, on purpose: payroll attributes are a separate act. */}
            <div className="text-12 text-ink-muted mt-0.5">Identity only — payroll terms are added as a profile version afterwards.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="new-emp-number" className={labelCls}>Employee number</label>
            <input id="new-emp-number" value={employeeNumber} onChange={e => setEmployeeNumber(e.target.value)} placeholder="E-001" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-emp-start" className={labelCls}>Start date</label>
            <input id="new-emp-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label htmlFor="new-emp-name" className={labelCls}>Full name</label>
            <input id="new-emp-name" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Ada Lovelace" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label htmlFor="new-emp-email" className={labelCls}>Email <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span></label>
            <input id="new-emp-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ada@example.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-emp-dob" className={labelCls}>Date of birth <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span></label>
            <input id="new-emp-dob" type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-emp-title" className={labelCls}>Job title <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span></label>
            <input id="new-emp-title" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Field Sales Agent" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-emp-phone" className={labelCls}>Phone <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span></label>
            <input id="new-emp-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+44 7700 900123" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-emp-region" className={labelCls}>Region <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span></label>
            <input id="new-emp-region" value={region} onChange={e => setRegion(e.target.value)} placeholder="North West" className={inputCls} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy || !valid}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={14} /> {busy ? 'Adding…' : 'Add employee'}
          </button>
          <button onClick={onClose} disabled={busy}
            className="k-btn-secondary flex-1 disabled:opacity-40">
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function HREmployees() {
  const { data, loading, error } = usePolledAsync(fetchEmployees, 10_000, [])
  const [employees, setEmployees] = useState<ApiEmployee[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof FILTERS)[number]>('all')
  // The drawer is keyed by id and reads its own detail (AUTH-1).
  const [detailId, setDetailId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => { if (data) setEmployees(data) }, [data])

  // Immediate refresh after a mutation (the 10s poll would catch it eventually).
  async function refresh() {
    try {
      setEmployees(await fetchEmployees())
    } catch {
      /* the poll will recover */
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const filtered = employees.filter(e => statusFilter === 'all' || e.status === statusFilter)
  const activeCount = employees.filter(e => e.status === 'active').length

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Employees"
        subtitle={`${filtered.length} employee${filtered.length !== 1 ? 's' : ''} · ${activeCount} active`}
        action={
          <button onClick={() => setCreating(true)}
            className="k-btn k-btn--lg inline-flex items-center gap-2">
            <Plus size={14} /> Add employee
          </button>
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState
            message="No employees here"
            hint={statusFilter === 'all'
              ? 'Add your first employee to start the payroll record.'
              : 'Nothing matches this status filter.'}
            actionLabel={statusFilter !== 'all' ? 'Show all' : undefined}
            onAction={() => setStatusFilter('all')}
          />
        ) : (
          /* Row grid: [number 80][name+email flex][NI 40 | freq 80 | salary 96 — or one honest absence span][status 80] */
          <div>
            {filtered.map(e => (
              <div key={e.id} onClick={() => setDetailId(e.id)}
                className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors">
                <span className="w-20 flex-shrink-0 text-12 text-ink-secondary truncate">{e.employeeNumber}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-13 font-medium text-ink-primary truncate">{e.fullName}</span>
                  {e.email && <span className="block text-12 text-ink-muted truncate">{e.email}</span>}
                </span>
                {e.current ? (
                  <>
                    <span className="w-10 flex-shrink-0 text-12 text-ink-secondary text-center">{e.current.niCategory}</span>
                    <span className="w-20 flex-shrink-0 text-12 text-ink-secondary">{FREQUENCY_LABEL[e.current.payFrequency] ?? e.current.payFrequency}</span>
                    <span className="w-24 flex-shrink-0 text-right text-13 font-medium text-ink-primary tabular-nums">{money(e.current.annualSalaryPence / 100)}</span>
                  </>
                ) : (
                  /* Width = the three payroll slots + their two gaps, so columns stay aligned. */
                  <span className="w-[248px] flex-shrink-0 text-12 text-ink-faint">No payroll profile</span>
                )}
                <span className="w-20 flex-shrink-0"><EmployeeStatus status={e.status} /></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailId && (
        <EmployeeDrawer
          employeeId={detailId}
          allEmployees={employees}
          onClose={() => setDetailId(null)}
          onChanged={() => void refresh()}
        />
      )}

      {creating && (
        <CreateEmployeeModal
          onClose={() => setCreating(false)}
          onCreated={created => { setCreating(false); void refresh(); setDetailId(created.id) }}
        />
      )}
    </div>
  )
}
